import type { GlobalToken } from 'antd';
import { useMemo } from 'react';
import { applyOpacity } from '../../../../utils/colors/apply-opacity';
import type { ThemeColor } from '../../../styles/theme';

export const getBaseLinkStyles = (themeToken?: GlobalToken) => ({
  textDecoration: '1px underline',
  textDecorationLine: 'underline',
  textDecorationThickness: '1px',
  textUnderlineOffset: `${themeToken?.marginXXS ?? 4}px`,
});

const getCssForTextColor = (color: string) => ({
  color: `${color}`,
  textDecorationColor: color,
  '> svg': {
    color: `${color}`,
  },
});

export const getTypographyLinkStyles = (
  themeToken: GlobalToken,
  hideDecorations: boolean,
  textColor?: GlobalToken['colorWhite'],
) => {
  const linkTextColor = textColor ?? themeToken.colorPrimary;
  const baseStyles = hideDecorations
    ? { textDecoration: 'none' }
    : getBaseLinkStyles(themeToken);

  const hoverStyles = {
    ...baseStyles,
    ...getCssForTextColor(
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      applyOpacity(linkTextColor as ThemeColor, 70),
    ),
  };

  const activeStyles = {
    ...baseStyles,
    ...getCssForTextColor(
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      applyOpacity(linkTextColor as ThemeColor, 85),
    ),
  };

  const styles = {
    cursor: 'pointer',
    ...baseStyles,
    ...getCssForTextColor(linkTextColor),
    '&:hover': hoverStyles,
    '@media (hover: hover)': {
      '&:hover': hoverStyles,
    },
    '&:active': activeStyles,
  };

  return {
    ...styles,
    '&.ant-btn-variant-link': {
      ...styles,
      '&:not(:disabled):not(.ant-btn-disabled)': styles,
      '&:not(.ant-btn-disabled)': styles,
      '&:hover': hoverStyles,
    },
  };
};

export const useGetTypographyLinkStyles = (
  themeToken: GlobalToken,
  hideDecorations: boolean,
  textColor?: GlobalToken['colorWhite'],
) =>
  useMemo(
    () => getTypographyLinkStyles(themeToken, hideDecorations, textColor),
    [hideDecorations, textColor, themeToken],
  );
